import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useCloudStorageLimit } from '@/hooks/useCloudStorageLimit';

export interface Recording {
  id: string;
  user_id: string;
  script_id: string | null;
  storage_path: string;
  filename: string;
  format: string;
  duration: number;
  file_size: number;
  created_at: string;
  updated_at: string;
}

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

// Type for database operations (table may not exist in generated types yet)
interface RecordingRow {
  id: string;
  user_id: string;
  script_id: string | null;
  storage_path: string;
  filename: string;
  format: string;
  duration: number;
  file_size: number;
  created_at: string;
  updated_at: string;
}

export function useRecordings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { canUpload } = useCloudStorageLimit();
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);

  // Fetch recordings for user or specific script
  const fetchRecordings = useCallback(async (scriptId?: string) => {
    if (!user) return [];
    
    setLoading(true);
    try {
      // recordings table exists in DB but not in generated types, use type assertion
      let query = supabase
        .from('recordings' as unknown as keyof Database['public']['Tables'])
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (scriptId) {
        query = query.eq('script_id', scriptId);
      }
      
      const { data, error } = await query;
      
      if (error) {
        // Table might not exist yet
        if (error.message.includes('does not exist')) {
          console.warn('Recordings table not yet created');
          setRecordings([]);
          return [];
        }
        throw error;
      }
      
      const typedData = (data || []) as unknown as RecordingRow[];
      setRecordings(typedData);
      return typedData;
    } catch (err) {
      console.error('Error fetching recordings:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Upload a recording to cloud
  const uploadRecording = useCallback(async (
    blob: Blob,
    options: {
      scriptId?: string;
      format?: string;
      duration?: number;
      filename?: string;
    } = {}
  ): Promise<Recording | null> => {
    if (!user) {
      toast({
        title: '請先登入',
        description: '需要登入才能保存錄音到雲端',
        variant: 'destructive',
      });
      return null;
    }

    // 檢查雲端儲存限制
    const storageCheck = canUpload(blob.size);
    if (!storageCheck.allowed) {
      if (storageCheck.reason === 'trial_expired') {
        toast({
          title: '試用已過期',
          description: '您的免費試用已結束，請升級至付費方案以繼續使用儲存功能',
          variant: 'destructive',
        });
      } else if (storageCheck.reason === 'monthly_limit') {
        toast({
          title: '已達本月上傳上限',
          description: '您本月的雲端儲存額度已用完，請升級方案或等待下月重置',
          variant: 'destructive',
        });
      } else {
        toast({
          title: '雲端儲存空間已滿',
          description: '請刪除舊錄音或升級方案以獲得更多空間',
          variant: 'destructive',
        });
      }
      return null;
    }

    setUploading(true);
    setUploadProgress({ loaded: 0, total: blob.size, percentage: 0 });

    try {
      const format = options.format || 'webm';
      const timestamp = Date.now();
      // User's chosen display name (can include Chinese)
      const displayFilename = options.filename || `recording_${timestamp}.${format}`;
      // Storage path must be ASCII-safe (Supabase rejects Chinese chars in keys)
      const storageFilename = `${timestamp}_${crypto.randomUUID().slice(0, 8)}.${format}`;
      const storagePath = `${user.id}/${storageFilename}`;

      // Upload to Supabase Storage using XHR for progress tracking
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      
      if (!accessToken) {
        throw new Error('認證失敗，請重新登入');
      }
      
      // Use XHR for upload progress tracking
      const uploadError = await new Promise<Error | null>((resolve) => {
        const xhr = new XMLHttpRequest();
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            // Reserve last 10% for database write phase
            const percentage = Math.round((e.loaded / e.total) * 90);
            setUploadProgress({ loaded: e.loaded, total: e.total, percentage });
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(null);
          } else {
            // Parse error message from response
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(new Error(response.message || response.error || `上傳失敗 (${xhr.status})`));
            } catch {
              resolve(new Error(`上傳失敗 (${xhr.status})`));
            }
          }
        });
        
        xhr.addEventListener('error', () => {
          resolve(new Error('網路錯誤，上傳失敗'));
        });
        
        xhr.addEventListener('abort', () => {
          resolve(new Error('上傳已取消'));
        });
        
        const url = `${SUPABASE_URL}/storage/v1/object/recordings/${storagePath}`;
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.setRequestHeader('Content-Type', `audio/${format}`);
        xhr.setRequestHeader('x-upsert', 'true'); // Allow overwriting
        xhr.send(blob);
      });

      if (uploadError) {
        // Check if bucket doesn't exist
        if (uploadError.message.includes('not found') || uploadError.message.includes('does not exist')) {
          throw new Error('雲端儲存未配置，請聯繫管理員');
        }
        throw uploadError;
      }

      // Storage upload complete, show 90% while database write happens
      setUploadProgress({ loaded: blob.size, total: blob.size, percentage: 90 });

      setUploadProgress({ loaded: blob.size, total: blob.size, percentage: 100 });

      // Create database record (table may not be in generated types yet)
      const insertData = {
        user_id: user.id,
        script_id: options.scriptId || null,
        storage_path: storagePath,
        filename: displayFilename,  // User's display name (can be Chinese)
        format,
        duration: options.duration || 0,
        file_size: blob.size,
      };
      
      const { data, error: dbError } = await supabase
        .from('recordings' as unknown as keyof Database['public']['Tables'])
        .insert(insertData)
        .select()
        .single();

      if (dbError) {
        // Table might not exist
        if (dbError.message.includes('does not exist')) {
          throw new Error('資料表未創建，請執行資料庫遷移');
        }
        throw dbError;
      }

      // Refresh recordings list
      await fetchRecordings(options.scriptId);

      return data as unknown as Recording;
    } catch (err) {
      const message = err instanceof Error ? err.message : '上傳失敗';
      // Enhanced Debug Logging
      console.group('Cloud Upload Error Debug');
      console.error('Full Error Object:', err);
      if (err instanceof Error) {
        console.error('Error Message:', err.message);
        console.error('Error Stack:', err.stack);
      }
      console.log('Upload Context:', {
        userId: user.id,
        filename: options.filename,
        fileSize: blob.size,
        storagePath: `${user.id}/${options.filename || 'unknown'}`
      });
      console.groupEnd();

      toast({
        title: '上傳失敗',
        description: `${message} (請查看控制台獲取詳細錯誤)`,
        variant: 'destructive',
      });
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }, [user, toast, fetchRecordings, canUpload]);

  // Get signed URL for a recording
  const getRecordingUrl = useCallback(async (storagePath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('recordings')
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    } catch (err) {
      console.error('Error getting recording URL:', err);
      return null;
    }
  }, []);

  // Delete a recording
  const deleteRecording = useCallback(async (recording: Recording): Promise<boolean> => {
    if (!user) return false;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('recordings')
        .remove([recording.storage_path]);

      if (storageError) {
        console.warn('Storage delete error:', storageError);
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('recordings' as unknown as keyof Database['public']['Tables'])
        .delete()
        .eq('id', recording.id)
        .eq('user_id', user.id);

      if (dbError) throw dbError;

      // Update local state
      setRecordings(prev => prev.filter(r => r.id !== recording.id));

      toast({
        title: '刪除成功',
        description: '錄音已從雲端刪除',
      });

      return true;
    } catch (err) {
      console.error('Error deleting recording:', err);
      toast({
        title: '刪除失敗',
        description: '無法刪除錄音',
        variant: 'destructive',
      });
      return false;
    }
  }, [user, toast]);

  return {
    recordings,
    loading,
    uploading,
    uploadProgress,
    fetchRecordings,
    uploadRecording,
    getRecordingUrl,
    deleteRecording,
  };
}
